import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/** Normalized stage ladder, mirrors order-tracking.ts on the backend. */
export type TrackingStage =
  | 'submitted'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'invoiced';

export interface OrderTracking {
  orderId: string;
  orderNumber: string | null;
  sfJobId: string | null;
  /** Raw SF status string (or stored fallback when degraded). */
  status: string | null;
  stage: TrackingStage;
  scheduledDate: string | null;
  completedDate: string | null;
  lastChecked: string;
  /** True when SF couldn't be read live and we fell back to stored status. */
  degraded: boolean;
}

interface ApiResponse {
  ok: boolean;
  orderId?: string;
  orderNumber?: string | null;
  sfJobId?: string | null;
  status?: string | null;
  stage?: TrackingStage;
  scheduledDate?: string | null;
  completedDate?: string | null;
  lastChecked?: string;
  degraded?: boolean;
  error?: string;
}

const KNOWN_STAGES: TrackingStage[] = [
  'submitted',
  'scheduled',
  'in_progress',
  'completed',
  'invoiced',
];

function normalizeStage(s: string | undefined): TrackingStage {
  return (KNOWN_STAGES as readonly string[]).includes(s ?? '')
    ? (s as TrackingStage)
    : 'submitted';
}

/**
 * useOrderTracking — live Service Fusion delivery status for one order.
 *
 * Calls the customer-scoped order-tracking function with the caller's
 * Supabase JWT. Returns null when not signed in or the order can't be
 * resolved. The function never 500s on an SF outage — it returns a
 * `degraded` payload built from the stored status instead, so the tracker
 * always has something to render.
 *
 * Used only on the order detail page (one live SF round-trip per view). The
 * Order-history list reads stored status off v_activity and does NOT hit this.
 */
export function useOrderTracking(orderId: string | null | undefined) {
  return useQuery<OrderTracking | null>({
    queryKey: ['order-tracking', orderId ?? null],
    enabled: !!orderId,
    queryFn: async (): Promise<OrderTracking | null> => {
      if (!orderId) return null;
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) return null;

      const res = await fetch(
        `/.netlify/functions/order-tracking?order_id=${encodeURIComponent(orderId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[useOrderTracking] order ${orderId}: HTTP ${res.status}`);
        return null;
      }
      const body = (await res.json()) as ApiResponse;
      if (!body.ok) return null;
      return {
        orderId: body.orderId ?? orderId,
        orderNumber: body.orderNumber ?? null,
        sfJobId: body.sfJobId ?? null,
        status: body.status ?? null,
        stage: normalizeStage(body.stage),
        scheduledDate: body.scheduledDate ?? null,
        completedDate: body.completedDate ?? null,
        lastChecked: body.lastChecked ?? new Date().toISOString(),
        degraded: body.degraded === true,
      };
    },
    // SF status doesn't move minute-to-minute; 45s keeps it fresh without
    // hammering SF on every revisit.
    staleTime: 45 * 1000,
  });
}
